import { EventEmitter } from "events";
import { Twist, Vec3 } from "../../../models/SyntheticMap";
import { SignalingConnection } from "../../../api/signaling/SignalingConnection";
import { arrayBufferToBase64 } from "../../../utils/ioUtils";
import * as THREE from "three";
import { BaseAgent } from "../agents/BaseAgent";
import { getCreds } from "../../../contexts/PageContext";
import { WorldRenderer } from "../../WorldRenderer";
import { RobotAgent } from "../robot/RobotAgent";
import { IAgent, ModelBasedVisual } from "../../../visuals/ModelBasedVisual";
import { copyVec3, isNotZero, toVector3 } from "../../../visuals/visual-utils";
import { MapBuilder } from "../robot/MapBuilder";
import { Scenario } from "../../../models/Scenario";
import { BaseVisual } from "../../../visuals/BaseVisual";

interface IObject {
  _id: string;
  id: string;
  position: Vec3;
  orientation: Vec3;
  type: string;
  subtype?: string;
  propBag?: any;
}

export interface SimAgentUpdate {
  _id: string;
  position: Vec3;
  orientation: Vec3;
  twist: Twist;
  actorStates: string[];
  discontinueStates: string[];
  path?: Vec3[]; // Path for the actor to follow
  objectToFollow?: string; // Optional field for following an object
  message?: any;
  properties?: { [key: string]: any }; // Optional properties for the actor
  objectToHighlight?: string; // Optional field for highlighting an object
  navGoal?: Vec3 | null; // Navigation goal for the actor
}

interface ScenarioParams {
  scenario: Scenario;
  objects: IObject[];
  pngMap: string;
  mapResolution: number;
}

export enum ScenarioStatus {
  Idle = 'idle',
  Building = 'building',
  Running = 'running'
}

export class CameraAgent extends BaseAgent {
  fakeWorld: THREE.Group;
  constructor() {
    const cameraIObj = createCameraIObject();
    const visual = new ModelBasedVisual();
    const fakeWorld = new THREE.Group();
    visual.obj = new THREE.Group();
    fakeWorld.add(visual.obj);
    visual._id = cameraIObj._id;
    visual.id = cameraIObj.id;
    super(visual);
    this.fakeWorld = fakeWorld;
  }
}

export interface AgentStateCapture {
  _id: string;
  position: THREE.Vector3;
  rotation: THREE.Euler;
  state: string;
}

export function createCameraIObject() {
  const sceneCamera: IObject = {
    _id: 'Scene Camera',
    id: 'Scene Camera',
    position: { x: 0, y: 0, z: 0 },
    orientation: { x: 0, y: 0, z: 0 },
    type: 'actor',
    subtype: 'SceneCamera',
    propBag: { speed: 0, states: ['Hide Terrain'] }
  };
  return sceneCamera;
}

export function scenarioCleanup(scenario: Scenario, objects: IObject[] = []) {
  scenario.blocks = scenario.blocks.filter(block => {
    if (objects.length > 0 && !objects.find(obj => obj._id === block.actorId)) {
      console.log(`Removing block ${block.id} because it has no corresponding object ${block.actorId}`);
      return false; // Keep blocks that have a corresponding object
    }
    if (!block.actorId) {
      return false; // Remove these blocks
    }
    block.outputs = block.outputs.filter(output => {
      if (!output.blockId) {
        return false; // Remove outputs without a target block
      }
      const targetBlock = scenario.blocks.find(b => b.id === output.blockId);
      if (!targetBlock) {
        return false; // Remove outputs that point to non-existent blocks
      }
      return true; // Keep outputs that point to existing blocks
    });
    return true; // Keep other blocks
  });
}

export class ScenarioClient extends EventEmitter implements IAgent {
  public connection: SignalingConnection;
  public status = ScenarioStatus.Idle;
  public agentMap: Map<string, BaseAgent> = new Map();
  private agentStartingStates: AgentStateCapture[] = [];
  private startCameraTarget = new THREE.Vector3();
  private startCameraPosition = new THREE.Vector3();
  constructor(public wr: WorldRenderer, public scenario: Scenario) {
    super();
    for (const [key, val] of wr.dataService.activeSite!.visuals) {
      if (val instanceof ModelBasedVisual) {
        const modelBased = val as ModelBasedVisual;
        if (modelBased.agent) {
          this.agentMap.set(modelBased._id, modelBased.agent as BaseAgent);
        }
      }
    }
    const { user, org } = getCreds();
    this.connection = new SignalingConnection({
      deviceId: org._id,
      group: 'techmage-scenario-server',
      token: org.token,
      receiver: this
    });
  }
  public isStopped = false;
  public stop() {
    this.connection.sendReplace({ stop: true });
    this.isStopped = true;
    for (const agent of this.agentMap.values()) {
      if (agent instanceof RobotAgent) {
        // IDK what to do here yet
      } else {
        agent.visual.velocity.linear = new THREE.Vector3(0, 0, 0);
        agent.visual.velocity.angular = new THREE.Vector3(0, 0, 0);
        agent.disableState('Walk');
      }
    }
  }

  public onStatus(status: string) {
    // this.status = status;
    if (status === 'stateChange: complete') {
      this.wr.dataService.scenarioClient = undefined;
      this.connection.close();
      if (this.status === ScenarioStatus.Running) {
        this.wr.cameraControls.setLookAt(
          this.startCameraPosition.x,
          this.startCameraPosition.y,
          this.startCameraPosition.z,

          this.startCameraTarget.x,
          this.startCameraTarget.y,
          this.startCameraTarget.z,

          true
        ).then(() => {
          for (const cap of this.agentStartingStates) {
            const agent = this.agentMap.get(cap._id);
            if (!agent || agent instanceof RobotAgent) continue;
            agent.visual.velocity.linear = new THREE.Vector3(0, 0, 0);
            agent.visual.velocity.angular = new THREE.Vector3(0, 0, 0);
            copyVec3(cap.position, agent.visual.position);
            copyVec3(cap.rotation, agent.visual.rotation);
            agent.visual.obj.position.copy(cap.position);
            agent.visual.obj.rotation.copy(cap.rotation);
            agent.enableState(cap.state);
          }
        })
          .catch(console.error);
      }
      this.status = ScenarioStatus.Idle;
      this.emit('status', 'completed');
    } else {
      this.emit('status', status);
    }
  }

  public cameraTargetVisual?: BaseVisual;
  public cameraTargetOffset = new THREE.Vector3();
  public cameraTargetLinear = new THREE.Vector3();

  public render(delta: number): boolean {
    if (this.cameraTargetVisual) {
      const siteObj = this.wr.dataService.activeSite!.obj;
      let tp = this.cameraTargetVisual.obj.position.clone();
      // if (this.cameraTargetVisual instanceof ModelBasedVisual) {
      //   const mb = this.cameraTargetVisual as ModelBasedVisual;
      //   const move = mb.velocity.linear.clone().multiplyScalar(delta);
      //   tp = tp.add(move);
      // }
      let cameraPos = tp.clone().add(this.cameraTargetOffset);
      cameraPos = cameraPos.applyMatrix4(siteObj.matrixWorld);
      const targetPos = tp.applyMatrix4(siteObj.matrixWorld);
      const camera = this.wr.camera;
      camera.position.copy(cameraPos);
      camera.lookAt(targetPos);
    }
    return false;
  }

  public async onUpdates(updates: SimAgentUpdate[]) {
    if (this.isStopped) {
      console.warn(`ScenarioClient is stopped, ignoring updates`);
      return;
    }
    for (const update of updates) {
      if (update._id === 'Scene Camera') {
        if (update.objectToFollow) {
          this.cameraTargetVisual = undefined;
          // Handle Scene Camera updates if necessary
          const targetVisual = this.wr.dataService.findVisual(update.objectToFollow);
          if (targetVisual) {
            const offset = toVector3(update.position);
            let cameraPos = targetVisual.obj.position.clone().add(offset);
            cameraPos.applyMatrix4(this.wr.dataService.activeSite!.obj.matrixWorld);
            const targetPos = targetVisual.obj.getWorldPosition(new THREE.Vector3());
            this.wr.cameraControls.setLookAt(
              cameraPos.x, cameraPos.y, cameraPos.z,
              targetPos.x, targetPos.y, targetPos.z, true)
              .then(() => {
                //console.log('looking completed');
                this.cameraTargetOffset = offset;
                this.cameraTargetVisual = targetVisual;
              })
              .catch(console.error);
          } else {
            this.cameraTargetVisual = undefined;
          }
        }

        if (update.actorStates.includes('Hide Terrain')) {
          this.wr.planetService.tileRenderingOnOff(false);
        } else if (update.discontinueStates.includes('Hide Terrain')) {
          this.wr.planetService.tileRenderingOnOff(true);
        }
        continue; // Skip Scene Camera updates for now
      }

      const agent = this.agentMap.get(update._id);
      if (!agent) {
        console.warn(`Actor with id ${update._id} not found`);
        continue; // Skip if actor is not found
      }
      if (update.objectToHighlight) {
        console.warn(`Highlighting objects not implemented`);
      }
      for (const key in update.properties) {
        agent.properties[key] = update.properties[key];
      }
      if (agent instanceof RobotAgent) {
        if (update.navGoal) {
          const robotAgent = agent as RobotAgent;
          robotAgent.setNavGoal(update.navGoal);
        }
        if (update.position.z > -1000) {
          agent.visual.obj.position.set(update.position.x, update.position.y, update.position.z);
        }
        if (update.orientation.z > -1000) {
          agent.visual.obj.rotation.set(update.orientation.x, update.orientation.y, update.orientation.z);
        }
      } else {
        agent.onSimUpdate(update);
      }
      if (update.message) {
        console.warn(`Actor ${update._id} received message`, update.message);
      }
      for (const actorState of update.actorStates) {
        // console.log({ actorState });
        await agent.enableState(actorState);
      }
      for (const discontinueState of update.discontinueStates) {
        // console.log(`Discontinuing state ${discontinueState} for actor ${update._id}`);
        await agent.disableState(discontinueState);
      }
      if (update.path) {
        agent.updateState({
          navPath:
            (update.path || []).flatMap(p => [p.x, p.y])
        });
      }
      agent.isDirty = true;
    }
  }

  public async build() {
    const objects = await this.indexObjects();
    // console.log({ objects });
    this.onStatus(`Building scenario ${this.scenario.name}`);
    scenarioCleanup(this.scenario, objects);
    console.log({ scenario: this.scenario });
    const connected = await this.connection.connect();
    if (!connected) {
      this.onStatus('Failed to connect to scenario server');
      return;
    }
    const mapBuilder = new MapBuilder(this.wr.dataService.activeSite!);
    const map = await mapBuilder.build(true, true);
    const base64PngMap = arrayBufferToBase64(map.data!);
    const params: ScenarioParams = {
      scenario: this.scenario,
      objects,
      pngMap: base64PngMap,
      mapResolution: mapBuilder.resolution
    };
    console.log('Sending scenario build request', params);
    this.status = ScenarioStatus.Building;
    this.connection.sendReplace({ build: params });
    // this.connection.close();
  }

  public async start() {
    this.onStatus(`Starting scenario ${this.scenario.name}`);
    const connected = await this.connection.connect();
    if (!connected) {
      this.onStatus('Failed to connect to scenario server');
      return;
    }
    const mapBuilder = new MapBuilder(this.wr.dataService.activeSite!);
    // const pngMap = await generatePngMap(this.uiService, 200, 0.5);
    // const base64PngMap = arrayBufferToBase64(pngMap);
    const params: ScenarioParams = {
      scenario: this.scenario,
      objects: await this.indexObjects(),
      pngMap: '',
      mapResolution: mapBuilder.resolution
    };
    this.startCameraTarget = this.wr.cameraControls.getTarget(new THREE.Vector3());
    this.startCameraPosition = this.wr.cameraControls.getPosition(new THREE.Vector3());
    this.agentStartingStates = [];
    for (const a of this.agentMap.values()) {
      this.agentStartingStates.push({
        _id: a.visual._id,
        position: a.visual.obj.position.clone(),
        rotation: a.visual.obj.rotation.clone(),
        state: a.getActiveState()
      });
    }
    this.status = ScenarioStatus.Running;
    this.wr.dataService.scenarioClient = this;
    this.connection.sendReplace({ start: params });
  }

  private async indexObjects() {
    const objects: IObject[] = [];
    for (const actor of this.agentMap.values()) {
      // const states = actor.states.filter(s => s.isTriggerable).map((state) => state.name);
      let subtype = '';
      if (actor instanceof RobotAgent) {
        subtype = 'Robot';
      }
      const actorObj: IObject = {
        _id: actor.visual._id,
        id: actor.visual.id,
        position: { x: 0, y: 0, z: 0 },
        orientation: { x: 0, y: 0, z: 0 },
        type: 'actor',
        subtype: subtype,
        propBag: {
          speed: actor.maxSpeed,
          states: actor.getStates().map(s => s.name)
        }
      };
      copyVec3(actor.visual.position, actorObj.position);
      copyVec3(actor.visual.rotation, actorObj.orientation);
      // debugger;
      objects.push(actorObj);
      // this.agentMap.set(actorObj.id, actor);
    }
    // Add Scene Camera as a special actor
    const sceneCamera = createCameraIObject();
    objects.push(sceneCamera);
    for (const visual of this.wr.dataService.activeSite!.visuals.values()) {
      if (this.agentMap.has(visual.id)) {
        continue;
      }
      if (visual.id) {
        const visualObject: IObject = {
          _id: visual.id,
          id: visual.id,
          position: { x: visual.position.x, y: visual.position.y, z: visual.position.z },
          orientation: { x: visual.rotation.x, y: visual.rotation.y, z: visual.rotation.z },
          type: visual.getType(),
          subtype: '',
          propBag: { speed: 0, states: [] }
        };
        objects.push(visualObject);
      }
    }
    //     orientation: { x: 0, y: 0, z: 0 },
    //     type: 'region'
    //   };
    //   objects.push(regionObject);
    // }
    return objects;
  }
}